import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle, FormControlLabel,
    Radio, RadioGroup,
    useMediaQuery,
    useTheme,
    TextField, Alert, Box
} from "@mui/material";
import React, {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import PropTypes from "prop-types";
import Spinner from "../../spinner/Spinner";
import {referralServices} from "../../../service/referralService";
import {connect} from "react-redux";
import {SettingsDialog as Dialog, SettingsDialogDescription, SettingsFormSection} from "../SettingsDialog";
import {getSettingsColors} from "../SettingsDesign";
import {alertActions} from "../../../store/actions/alertActions";

export function Withdraw(props) {

    const {onClose, open, successMsg, clear} = props;

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [paymentMethod, setPaymentMethod] = useState(null);
    const [loading, setLoading] = useState(false);

    const {register, handleSubmit, setValue, formState: {errors}} = useForm({defaultValues: {paymentMethod: "paypal", account: ""}});
    const [formError, setFormError] = useState("");

    const onSubmit = async (data) => {

        setLoading(true);
        setFormError("");
        let formData = {
            method: data?.paymentMethod,
            account: data?.account
        };

        if (paymentMethod?.id > 0) {
            await referralServices.UpdatePaymentMethod(paymentMethod?.id, formData).then(() => {
                setLoading(false);
                successMsg("Payment method successfully updated.");
                handleClose();
            }).catch((e) => {
                setFormError(e?.response?.data?.error || "Unable to save payment details. Please try again.");
                setLoading(false);
            });
        } else {
            await referralServices.AddPaymentMethod(formData).then(() => {
                setLoading(false);
                successMsg("Payment method successfully added.");
                handleClose();
            }).catch((e) => {
                setFormError(e?.response?.data?.error || "Unable to save payment details. Please try again.");
                setLoading(false);
            });
        }
    };

    const handleClose = (event, reason) => {
        if (reason === 'backdropClick') {
            return false;
        }

        setTimeout(() => {
            clear();
        }, 3000);

        setPaymentMethod(null);
        onClose();
    };

    useEffect(() => {
        async function fetchPaymentMethod(): void {
            setLoading(true);
            setFormError("");
            await referralServices.findPaymentMethod().then((result) => {
                setLoading(false);
                if (result?.id > 0) {
                    setPaymentMethod(result);
                    setValue("paymentMethod", result?.method);
                    setValue("account", result?.account);
                }
            }).catch((e) => {
                setFormError(e?.response?.data?.error || "Unable to save payment details. Please try again.");
            }).finally(() => {
                setLoading(false);
            });
        }

        if (open) {
            fetchPaymentMethod();
        }

    }, [open, setValue]);

    return (
        <Dialog open={open}
                onClose={handleClose}
                fullWidth={true}
                fullScreen={fullScreen}
                aria-labelledby="withdraw-form"
                maxWidth={"sm"}
        >
            <DialogTitle id="withdraw-form">Withdrawal payment details
                <SettingsDialogDescription>Choose where to receive your referral commission payments.</SettingsDialogDescription>
            </DialogTitle>
            <DialogContent>
                {formError && <Alert severity="error" variant="filled" sx={{mb: 2}}>{formError}</Alert>}
                <form onSubmit={handleSubmit(onSubmit)} autoComplete={"off"}>
                    <Box sx={{bgcolor: theme => getSettingsColors(theme).inset, borderRadius: "10px", px: 2, py: 1}}>
                    <RadioGroup aria-label="paymentMethod" name="paymentMethod" value="paypal">
                        <FormControlLabel
                            value="paypal" control={<Radio checked color="primary"/>} label="PayPal"
                        />
                    </RadioGroup>
                    </Box>
                    <SettingsFormSection title="PayPal account" description="Enter the account where you want to receive payments.">
                    <TextField
                        error={!!errors?.account}
                        helperText={errors?.account ? "Enter a valid account (3–255 characters)." : undefined}
                        disabled={loading}
                        type="text"
                        variant="outlined"
                        margin="dense"
                        size="small"
                        label="PayPal account"
                        name="account"
                        fullWidth
                        id="account"
                        {...register("account", {required: true, maxLength: 255, minLength: 3})}
                        placeholder="Enter your PayPal account"
                        slotProps={{
                            inputLabel: {
                                shrink: true
                            }
                        }}
                    />
                    </SettingsFormSection>
                </form>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="inherit" disabled={loading}>
                    Cancel
                </Button>
                {loading ? <Spinner/> :
                    <Button onClick={handleSubmit(onSubmit)} color="primary" variant="contained">
                        Save & Close
                    </Button>
                }
            </DialogActions>
        </Dialog>
    );
}

Withdraw.prototype = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired
};

const mapStateToProps = (state) => {
    return {
        state: {
            alert: state.alert,
        }
    };
};

const mapDispatchToProps = {
    errorMsg: alertActions.error,
    successMsg: alertActions.success,
    clear: alertActions.clear,
};

export default connect(mapStateToProps, mapDispatchToProps)(Withdraw);